import React from "react";
import { Chart } from "react-google-charts";
import { Typography, Box, CircularProgress } from "@mui/material";

const PlaysPerQuickTaleChart = ({
  perQuickTaleData,
  loading,
  selectedQuickTaleId = null,
  onSelectQuickTale,
  rangeLabel,
}) => {
  // Total plays across all quicktales in the current period.
  const totalPlays = perQuickTaleData
    ? perQuickTaleData.reduce((sum, item) => sum + (item.count || 0), 0)
    : 0;

  // Summary row: date range on the left, total plays for the period.
  const renderSummary = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 4,
        mb: 1,
        flexWrap: "wrap",
      }}
    >
      {rangeLabel && (
        <Typography sx={{ fontFamily: "Avenir Book, sans-serif", fontSize: 13, color: "#666" }}>
          {rangeLabel}
        </Typography>
      )}
      <Typography sx={{ fontFamily: "Avenir Book, sans-serif", fontSize: 13, color: "#666" }}>
        Total Plays: <strong>{totalPlays}</strong>
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!perQuickTaleData || perQuickTaleData.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        {renderSummary()}
        <Typography
          variant="h6"
          sx={{ fontFamily: "Avenir Book, sans-serif", color: "#333", mb: 2 }}
        >
          Plays per QuickTale
        </Typography>
        <Typography sx={{ fontFamily: "Avenir Book, sans-serif", color: "#999" }}>
          No play data for this period.
        </Typography>
      </Box>
    );
  }

  // Build the chart data. Include a per-bar style column so we can highlight
  // the currently selected quicktale's bar.
  const defaultColor = "#00a7cd";
  const selectedColor = "#0B47A1";

  const data = [
    ["QuickTale", "Plays", { role: "style" }],
    ...perQuickTaleData.map((item) => [
      item.title,
      item.count,
      `color: ${
        selectedQuickTaleId && item.objectId === selectedQuickTaleId
          ? selectedColor
          : defaultColor
      }`,
    ]),
  ];

  // Calculate dynamic height based on number of items
  const chartHeight = Math.max(400, perQuickTaleData.length * 25 + 150);

  const options = {
    title: "Plays per QuickTale (click a bar to filter Plays per Day)",
    titleTextStyle: {
      color: "#333",
      fontSize: 16,
      bold: true,
      fontFamily: "Avenir Book, sans-serif",
    },
    fontFamily: "Avenir Book, sans-serif",
    legend: { position: "none" },
    bar: { groupWidth: "60%" },
    vAxis: {
      minValue: 0,
      format: "0",
      textStyle: { fontFamily: "Avenir Book, sans-serif", fontSize: 12 },
    },
    hAxis: {
      textStyle: {
        fontFamily: "Avenir Book, sans-serif",
        fontSize: 10,
      },
      slantedText: true,
      slantedTextAngle: 45,
    },
    // Use fixed pixel offsets so the plot area doesn't leave a large gap at the
    // top when the chart is tall (many quicktales). `bottom` reserves room for
    // the slanted date/title labels.
    chartArea: { width: "85%", top: 60, bottom: 120 },
    animation: { startup: true, duration: 500, easing: "out" },
  };

  // react-google-charts event handler: when a bar is selected, resolve the row
  // index back to the corresponding quicktale and notify the parent.
  const chartEvents = [
    {
      eventName: "select",
      callback: ({ chartWrapper }) => {
        const chart = chartWrapper.getChart();
        const selection = chart.getSelection();
        if (!selection || selection.length === 0) return;

        const { row } = selection[0];
        if (row === null || row === undefined) return;

        const item = perQuickTaleData[row];
        if (!item || !item.objectId) return;

        if (onSelectQuickTale) {
          // Toggle off if clicking the already-selected bar
          if (item.objectId === selectedQuickTaleId) {
            onSelectQuickTale(null);
          } else {
            onSelectQuickTale(item.objectId, item.title);
          }
        }
      },
    },
  ];

  return (
    <Box>
      {renderSummary()}
      <Chart
        chartType="ColumnChart"
        width="100%"
        height={`${chartHeight}px`}
        data={data}
        options={options}
        chartEvents={chartEvents}
      />
    </Box>
  );
};

export default PlaysPerQuickTaleChart;
